
import { Component, Vue, Prop, Watch } from "vue-property-decorator"

import { get, set } from "lodash"

// @ts-ignore
import { Datetime } from "vue-datetime"
import vSelect from "vue-select"

import moment from "moment"

@Component({
	components: {
		"v-select": vSelect,
		"datetime": Datetime
	}
})
export default class DatePicker extends Vue {
	$refs!: {
		minute: HTMLInputElement,
		hour: HTMLInputElement,
		day: HTMLInputElement[],
		month: HTMLInputElement[],
		year: HTMLInputElement[]
	}

	@Prop()
	value!: string | Date | null;

	@Prop()
	includeTime!: boolean;

	@Prop({ default: true })
	includeYesterday!: boolean;

	@Prop({ default: true })
	includeToday!: boolean;

	@Prop({ default: false })
	readonly!: boolean;

	@Prop({ default: true })
	convertDatesToUTC!: boolean;

	@Prop({ default: false })
	enterAsUTC!: boolean;

	public dateValueParsed: Date | null = null;
	public dateValueISO: string | null = null;

	public dateParts: any[] = [];

	ampm: any = null;
	ampmList = ["AM", "PM"];

	year: string = "";
	month: string = "";
	day: string = "";
	hour: string = "";
	minute: string = "";

	dateIsInvalid: boolean = false;
	timeIsInvalid: boolean = false;

	mounted () {
		this.dateParts = [
			{
				key: "day",
				label: "Day",
				formatPart: "d",
				order: 1,
				styling: "min-width: 50px"
			},
			{
				key: "month",
				label: "Month",
				formatPart: "m",
				order: 2,
				styling: "min-width: 50px"
			},
			{
				key: "year",
				label: "Year",
				formatPart: "y",
				order: 3,
				styling: "min-width: 50px"
			}
		]

		this.setValue(this.value)

		// Sort dateparts based on locale
		const dateLocale = (moment.localeData() as any)._longDateFormat.L.toLowerCase()

		this.dateParts.forEach((datePart:any) => {
			datePart.order = dateLocale.indexOf(datePart.formatPart)
		})
		this.dateParts.sortBy("order")
	}

	@Watch("value")
	valueChanged (value: string) {
		this.setValue(value)
	}

	/**
	 * If days, months or years have been entered
	 */
	public get anyDateValue (): boolean {
		return !!this.day ||
			!!this.month ||
			!!this.year
	}

	/**
	 * If hours or minutes have been entered
	 */
	public get anyTimeValue (): boolean {
		return !!this.hour ||
			!!this.minute
	}

	/**
	 * Upate text boxes with value model change
	 */
	private setValue (value: string | Date | null) {
		let dateValue = null
		try {
			dateValue = value !== null && value !== ""
				? (value instanceof Date)
					? value
					: new Date(value)
				: null
		} catch {
		}

		if (dateValue !== null && !isNaN(dateValue.getTime())) {
			this.dateValueParsed = dateValue
			this.dateValueISO = this.dateValueParsed.toISOString()

			if (this.enterAsUTC) {
				this.year = this.dateValueParsed.getUTCFullYear().toString()
				this.month = (this.dateValueParsed.getUTCMonth() + 1).toString()
				this.day = this.dateValueParsed.getUTCDate().toString()
			} else {
				this.year = this.dateValueParsed.getFullYear().toString()
				this.month = (this.dateValueParsed.getMonth() + 1).toString()
				this.day = this.dateValueParsed.getDate().toString()
			}

			if (this.includeTime || !this.enterAsUTC) {
				const hours = this.dateValueParsed.getHours()
				this.hour = (hours > 12 ? hours - 12 : hours).toString()

				this.minute = this.dateValueParsed.getMinutes().toString()
				this.ampm = this.dateValueParsed.getHours() >= 12 ? "PM" : "AM"
			} else {
				this.hour = ""
				this.minute = ""
			}
		} else {
			this.dateValueParsed = null
			this.ampm = null
			this.year = ""
			this.month = ""
			this.day = ""
			this.hour = ""
			this.minute = ""
		}
	}

	/**
	 * Update value using text box values
	 */
	updateDate () {
		let dateString = this.$refs.year[0].value + "-" +
			this.$refs.month[0].value + "-" + this.$refs.day[0].value

		const momentDate = moment.utc(dateString, "YYYY-M-D", true)

		if (momentDate.isValid()) {
			this.dateIsInvalid = false
		} else {
			this.dateIsInvalid = true

			if (this.hour) {
				const hours = parseInt(this.hour)
				if (isNaN(hours) || hours > 23 || hours < 0) {
					this.timeIsInvalid = true
				}
			}

			if (this.minute) {
				const minutes = parseInt(this.minute)
				if (isNaN(minutes) || minutes > 59 || minutes < 0) {
					this.timeIsInvalid = true
				}
			}

			return
		}

		let momentDateTime: moment.Moment

		let hours = this.$refs.hour.value
		if (!hours && !this.includeTime) {
			hours = "0"
		}

		if (hours === "0") {
			hours = "12"
		}

		let hoursValue = parseInt(hours)
		if (hoursValue > 12) {
			hoursValue = hoursValue - 12
			this.ampm = "PM"
		}

		let minutes = this.$refs.minute.value
		if (!minutes && !this.includeTime) {
			minutes = "0"
		}

		if (!this.ampm && !this.includeTime) {
			this.ampm = "AM"
		}

		dateString = dateString + " " + hoursValue + ":" + minutes + ":00 " + this.ampm
		if (this.enterAsUTC) {
			momentDateTime = moment.utc(dateString, "YYYY-M-D h:m:ss a", true)
		} else {
			momentDateTime = moment(dateString, "YYYY-M-D h:m:ss a", true)
		}

		if (momentDateTime.isValid()) {
			this.timeIsInvalid = false
			const newValue = momentDateTime.utc().toDate()
			this.$emit("input", newValue)
		} else {
			this.timeIsInvalid = true
			this.dateIsInvalid = true
		}
	}

	getValuePart (datePart: string) {
		return get(this, datePart)
	}
	setValuePart (datePart: string, value: string) {
		return set(this, datePart, value)
	}

	clearDate () {
		this.setValue(null)
		this.$emit("input", null)
	}

	openCalender (event: any, which: string) {
		(this.$refs as any)[which].open(event)
	}

	async setDate (newDate: Date) {
		this.setValue(newDate)
		await Vue.nextTick()

		this.updateDate()
	}

	yesterday () {
		let date = new Date()
		date.setDate(date.getDate() - 1)
		this.setDate(date)
	}

	today () {
		this.setDate(new Date())
	}

	tomorrow () {
		let date = new Date()
		date.setDate(date.getDate() + 1)
		this.setDate(date)
	}
}

